import React, { FC, useState } from 'react';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useStorage } from '~Components/Storage';
import NumberInput from '~ComponentsImproved/Numpad';
import { useDispatch, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import HandyPageWrapper from '~Scenes/Handy/Components/HandyPageWrapper';
import HandySettingsBlock, {
  SettingBlockItemType,
} from '~Scenes/Handy/Components/HandySettingsBlock';
import { IconType } from '~Scenes/Handy/Components/RoundedIcon';
import { PageNames, usePage } from '~Scenes/Handy/Context/PageProvider';
import SettingsV2, { PageType } from '~Scenes/Settings-v2';
import * as actions from '~actions';

/** Maps a Settings-v2 page to the Handy page that renders it, so TopBar and the slide work as usual */
const HANDY_PAGE_FOR: Record<PageType, PageNames | null> = {
  printers: 'printerSettings',
  'kitchen-tickets': 'kitchenTicketsSettings',
  layout: 'posLayoutSettings',
  payments: 'paymentSettings',
  receipts: 'receiptSettings',
  tables: 'tableSettings',
  blueprints: 'blueprintSettings',
  'staff-settings': 'staffSettings',
  safT: 'safT',
  ejournal: null,
  other: 'general',
  developer: 'developer',
  KDS: null,
  app: null,
};

const HandySettings: FC = () => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const logger = useLogger('handy-settings');
  const dispatch = useDispatch();
  const page = usePage();
  const deviceHandler = useDeviceHandler();
  const [storage] = useStorage();

  const zones = useSelector((state) => state.zones);
  const companyFlags = useSelector((state) => state.companyFlags);

  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);

  const hasOrderLicense = licenses.hasHandheldOrder || licenses.hasHandheldPro;

  const hasDeviceClaimed = companyFlags.useNewLicenses
    ? Boolean(deviceHandler.activeDevice?.id)
    : Boolean(storage.taxComplianceDeviceId);

  const navigationBlocks: SettingBlockItemType[] = [
    {
      id: 'printer-settings',
      title: i18n('Handy.Settings.PrintersTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.PRINTER,
        bgColor: 'rgb(7 122 180)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('printerSettings');
        },
      },
    },
    {
      id: 'kitchen-tickets-settings',
      title: i18n('Handy.Settings.KitchenTickets'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.KITCHEN_TICKET,
        bgColor: 'rgb(116 112 177)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('kitchenTicketsSettings');
        },
      },
    },
    {
      id: 'payment-settings',
      title: i18n('Handy.Settings.PaymentsTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.PAYMENTS,
        bgColor: 'rgb(88 161 88)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('paymentSettings');
        },
      },
    },
    {
      id: 'receipt-settings',
      title: i18n('Handy.Settings.ReceiptsTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.RECEIPT,
        bgColor: 'rgb(240 136 75)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('receiptSettings');
        },
      },
    },
    {
      id: 'table-settings',
      title: i18n('Settings.Tables'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.RESTAURANT,
        bgColor: 'rgb(67 140 198)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('tableSettings');
        },
      },
    },
    {
      id: 'staff-settings',
      title: i18n('Handy.Settings.StaffTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.STAFF,
        bgColor: 'rgb(0, 0, 0)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('staffSettings');
        },
      },
    },
    {
      id: 'device-settings',
      title: i18n('Handy.Settings.DeviceTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.CHECK,
        bgColor: 'rgb(198 166 67)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          if (hasDeviceClaimed) {
            setShowPasswordInput(true);
          } else {
            page.navigateTo('deviceSettings');
          }
        },
      },
    },
    {
      id: 'general-settings',
      title: i18n('Handy.Settings.GeneralTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.COG_WHEEL,
        bgColor: 'rgb(131 131 131)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('general');
        },
      },
    },
  ];

  if (zones.length > 0) {
    navigationBlocks.splice(5, 0, {
      id: 'zone-settings',
      title: i18n('Settings.Zone'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.ZONE,
        bgColor: 'rgb(217, 172, 152)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('zoneSettings');
        },
      },
    });
  }

  if (hasOrderLicense) {
    const posLayoutSettings: SettingBlockItemType = {
      id: 'pos-layout-settings',
      title: i18n('Handy.Settings.PosLayoutTitle'),
      bigTitle: true,
      prefixIcon: {
        type: IconType.LAYOUT_CHANGE,
        bgColor: 'rgb(190 103 155)',
      },
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('posLayoutSettings');
        },
      },
    };

    navigationBlocks.splice(2, 0, posLayoutSettings);
  }

  return (
    <>
      <HandyPageWrapper pageTitle={i18n('Handy.Settings')}>
        <SettingsV2
          isHandy
          onNavigateHandy={(settingsPage) => {
            const handyPage = HANDY_PAGE_FOR[settingsPage];
            if (handyPage) {
              page.navigateTo(handyPage);
            }
          }}
        />
        {/* <HandySettingsBlock items={navigationBlocks} /> */}
      </HandyPageWrapper>

      {showPasswordInput && (
        <NumberInput
          onClose={() => {
            setShowPasswordInput(false);
          }}
          onSubmit={(value) => {
            if (value !== '1111') {
              logger.info('Tried to change device without correct password');
              dispatch(actions.addToast(i18n('Settings.NotAllowed'), true, 'notallowed', true));
            } else {
              setShowPasswordInput(false);
              page.navigateTo('deviceSettings');
            }
          }}
        />
      )}
    </>
  );
};

export default HandySettings;
